import { useNavigate } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  doc,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import {
  ref as sRef,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import { db, storage } from "../../Firebase";
import "../../Admin.css";
import AdminNav from "../../components/AdminNav";

// ---------- helpers ----------
function slugSafeFilename(name) {
  return String(name || "photo")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^a-zA-Z0-9._-]/g, "")
    .slice(0, 80);
}

function nowISODate() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function normalizePriority(p) {
  const v = (p || "").toLowerCase();
  if (v.includes("immed")) return "Immediate";
  if (v.includes("near")) return "Near-term";
  return "Monitor";
}

function deriveCounts(findings = []) {
  let immediate = 0, nearTerm = 0, monitor = 0;
  for (const f of findings) {
    const p = normalizePriority(f.priority);
    if (p === "Immediate") immediate++;
    else if (p === "Near-term") nearTerm++;
    else monitor++;
  }
  return { immediate, nearTerm, monitor };
}

function makeFindingId(idx) {
  return `F-${String(idx + 1).padStart(2, "0")}`;
}

// ---------- component ----------
export default function AdminReportCreate() {
  const nav = useNavigate();

  // Create a docRef ONCE so we have blobId for Storage paths immediately
  const docRefOnce = useRef(null);
  if (!docRefOnce.current) {
    docRefOnce.current = doc(collection(db, "publicReports"));
  }
  const reportDocRef = docRefOnce.current;
  const blobId = reportDocRef.id;

  const [saving, setSaving] = useState(false);
  const [statusMsg, setStatusMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [hasSavedOnce, setHasSavedOnce] = useState(false);

  //TESTING
  async function testWrite() {
  try {
    const ref = doc(db, "publicReports", "TEST_REPORT_DOC");
    await setDoc(ref, {
      status: "draft",
      propertyName: "TEST WRITE — delete me",
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    alert("✅ Test doc written: publicReports/TEST_REPORT_DOC");
  } catch (e) {
  console.error("FULL ERROR:", e);
  alert(`${e?.code || "no-code"}: ${e?.message || e}`);
}
}

  // Minimal v1 state
  const [report, setReport] = useState({
    status: "draft",
    title: "Commercial Roof Inspection Report",
    propertyName: "",
    addressLine1: "",
    cityStateZip: "",

    inspectedOn: nowISODate(),
    reportDate: nowISODate(),

    preparedFor: "Facility Management Team",
    preparedBy: {
      company: "Crowned Roofing",
      phone: "",
      email: "",
    },

    roofSystemsText: "Standing Seam Metal", // comma-separated input
    approxArea: "",
    access: "",
    weather: "",
    occupancySensitivity: "",

    summaryBullets: [
      "",
      "",
      "",
    ],

    findings: [
      {
        id: "F-01",
        priority: "Immediate",
        title: "",
        location: "",
        condition: "",
        impact: "",
        recommendation: "",
        photos: [], // { path, caption, url? }
      },
    ],

    limitations: [
      "This report reflects conditions observed on the inspection date listed above.",
      "Inspection was visual and non-destructive unless specifically stated in the findings."
    ],
  });

  // keep doc title nice in browser
  useEffect(() => {
    document.title = "New Report | Admin";
  }, []);

  const counts = useMemo(() => deriveCounts(report.findings), [report.findings]);

  const roofSystems = useMemo(() => {
    return String(report.roofSystemsText || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }, [report.roofSystemsText]);

  function setField(path, value) {
    // simple path setter: "preparedBy.phone"
    setReport((prev) => {
      const next = structuredClone(prev);
      const parts = path.split(".");
      let cur = next;
      for (let i = 0; i < parts.length - 1; i++) {
        cur = cur[parts[i]];
      }
      cur[parts[parts.length - 1]] = value;
      return next;
    });
  }

  function setFinding(idx, field, value) {
    setReport((prev) => {
      const next = structuredClone(prev);
      next.findings[idx][field] = value;
      return next;
    });
  }

  function addFinding() {
    setReport((prev) => {
      const next = structuredClone(prev);
      const id = makeFindingId(next.findings.length);
      next.findings.push({
        id,
        priority: "Monitor",
        title: "",
        location: "",
        condition: "",
        impact: "",
        recommendation: "",
        photos: [],
      });
      return next;
    });
  }

  function removeFinding(idx) {
    setReport((prev) => {
      const next = structuredClone(prev);
      next.findings.splice(idx, 1);
      // re-number to keep F-01, F-02...
      next.findings = next.findings.map((f, i) => ({ ...f, id: makeFindingId(i) }));
      return next;
    });
  }

  async function uploadPhotosForFinding(findingIdx, files) {
    if (!files || !files.length) return;

    setErrorMsg("");
    setStatusMsg("Uploading photos…");

    const findingId = report.findings[findingIdx].id;

    const uploaded = [];
    for (const file of files) {
      const safeName = slugSafeFilename(file.name);
      const path = `publicReports/${blobId}/photos/${findingId}/${Date.now()}-${safeName}`;

      const storageRef = sRef(storage, path);
      await uploadBytes(storageRef, file);

      let url = "";
      try {
        url = await getDownloadURL(storageRef);
      } catch {
        url = "";
      }

      uploaded.push({
        path,
        caption: "",
        url, // preview only; we won't store url in Firestore
      });
    }

    setReport((prev) => {
      const next = structuredClone(prev);
      next.findings[findingIdx].photos = [
        ...(next.findings[findingIdx].photos || []),
        ...uploaded,
      ];
      return next;
    });

    setStatusMsg("Photos uploaded.");
    window.setTimeout(() => setStatusMsg(""), 1200);
  }

  function updatePhotoCaption(findingIdx, photoIdx, caption) {
    setReport((prev) => {
      const next = structuredClone(prev);
      next.findings[findingIdx].photos[photoIdx].caption = caption;
      return next;
    });
  }

  function removePhoto(findingIdx, photoIdx) {
    // Note: this removes from Firestore payload; it does NOT delete from Storage (safe for v1)
    setReport((prev) => {
      const next = structuredClone(prev);
      next.findings[findingIdx].photos.splice(photoIdx, 1);
      return next;
    });
  }

  function toFirestorePayload(nextStatus) {
    // strip preview URLs; store only Storage paths + caption
    const findings = (report.findings || []).map((f) => ({
      id: f.id,
      priority: normalizePriority(f.priority),
      title: f.title || "",
      location: f.location || "",
      condition: f.condition || "",
      impact: f.impact || "",
      recommendation: f.recommendation || "",
      photos: (f.photos || []).map((p) => ({
        path: p.path,
        caption: p.caption || "",
      })),
    }));

    return {
      status: nextStatus || report.status || "draft",
      title: report.title || "Commercial Roof Inspection Report",

      propertyName: report.propertyName || "",
      addressLine1: report.addressLine1 || "",
      cityStateZip: report.cityStateZip || "",

      inspectedOn: report.inspectedOn || "",
      reportDate: report.reportDate || "",

      preparedFor: report.preparedFor || "",
      preparedBy: {
        company: report.preparedBy?.company || "Crowned Roofing",
        phone: report.preparedBy?.phone || "(346) 871 5126",
        email: report.preparedBy?.email || "info@crownedroofing.com",
      },

      roofSystems,
      approxArea: report.approxArea || "",
      access: report.access || "",
      weather: report.weather || "",
      occupancySensitivity: report.occupancySensitivity || "",

      summaryBullets: (report.summaryBullets || []).map((s) => String(s || "")),

      // counts derived (you can override later if desired)
      counts,

      findings,

      limitations: (report.limitations || []).map((s) => String(s || "")),

      // timestamps
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      ...(nextStatus === "published" ? { publishedAt: serverTimestamp() } : {}),
    };
  }

  async function save(nextStatus) {
    setSaving(true);
    setErrorMsg("");
    setStatusMsg("");

    try {
      const payload = toFirestorePayload(nextStatus);

      // IMPORTANT: createdAt should not overwrite if saving again; merge true prevents nuking fields,
      // but serverTimestamp() will still update createdAt each save if included.
      // Easiest v1: write createdAt only on first save by checking local flag:
      // For now, we keep it simple; if you want strict createdAt, we’ll adjust in v2.

      await setDoc(reportDocRef, payload, { merge: true });
      setHasSavedOnce(true);

      setReport((prev) => ({
        ...prev,
        status: payload.status,
      }));

      setStatusMsg(nextStatus === "published" ? "Report published." : "Draft saved.");
      window.setTimeout(() => setStatusMsg(""), 1500);
      return true;
    } catch (e) {
      setErrorMsg(e?.message || "Failed to save report.");
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function previewReport() {
    const previewUrl = `/report/${blobId}?preview=1`;

    if (!hasSavedOnce) {
      const ok = await save(report.status || "draft");
      if (!ok) return;
    }

    window.open(previewUrl, "_blank", "noopener,noreferrer");
  }

  return (
    <div className="arcPage">
      <AdminNav />
      <div className="arcTop">
        <div className="arcTopLeft">
          <h1>Create Report</h1>
          <div className="arcMeta">
            <div><span>Blob ID:</span> <strong>{blobId}</strong></div>
            <div><span>Status:</span> <strong className="arcStatus">{report.status}</strong></div>
          </div>
        </div>

        <div className="arcActions">
          <button className="btn" disabled={saving} onClick={() => save("draft")}>
            Save Draft
          </button>
          <button className="btn btnPrimary" disabled={saving} onClick={() => save("published")}>
            Publish
          </button>
          <button className="btn btnGhost" type="button" disabled={saving} onClick={previewReport}>
            Preview Report
          </button>
        </div>
      </div>

      {statusMsg ? <div className="arcNotice">{statusMsg}</div> : null}
      {errorMsg ? <div className="arcError">{errorMsg}</div> : null}

      {/* BASIC INFO */}
      <section className="card">
        <div className="cardHeader">
          <h2>Report Info</h2>
          <p>Commercial-first cover data and identity.</p>
        </div>

        <div className="grid2">
          <label className="field">
            <span>Report Title</span>
            <input
              value={report.title}
              onChange={(e) => setField("title", e.target.value)}
              placeholder="Commercial Roof Inspection Report"
            />
          </label>

          <label className="field">
            <span>Prepared For</span>
            <input
              value={report.preparedFor}
              onChange={(e) => setField("preparedFor", e.target.value)}
              placeholder="Facility Management Team"
            />
          </label>

          <label className="field">
            <span>Property / Facility Name</span>
            <input
              value={report.propertyName}
              onChange={(e) => setField("propertyName", e.target.value)}
              placeholder="Industrial Facility — Roof Condition Review"
            />
          </label>

          <label className="field">
            <span>Address Line 1</span>
            <input
              value={report.addressLine1}
              onChange={(e) => setField("addressLine1", e.target.value)}
              placeholder="1234 Industrial Park Dr"
            />
          </label>

          <label className="field">
            <span>City, State ZIP</span>
            <input
              value={report.cityStateZip}
              onChange={(e) => setField("cityStateZip", e.target.value)}
              placeholder="Houston, TX 77041"
            />
          </label>

          <div className="grid2tight">
            <label className="field">
              <span>Inspected On</span>
              <input
                type="date"
                value={report.inspectedOn}
                onChange={(e) => setField("inspectedOn", e.target.value)}
              />
            </label>

            <label className="field">
              <span>Report Date</span>
              <input
                type="date"
                value={report.reportDate}
                onChange={(e) => setField("reportDate", e.target.value)}
              />
            </label>
          </div>
        </div>

        <div className="divider" />

        <div className="grid3">
          <label className="field">
            <span>Prepared By — Company</span>
            <input
              value={report.preparedBy.company}
              onChange={(e) => setField("preparedBy.company", e.target.value)}
              placeholder="Crowned Roofing"
            />
          </label>
          <label className="field">
            <span>Prepared By — Phone</span>
            <input
              value={report.preparedBy.phone}
              onChange={(e) => setField("preparedBy.phone", e.target.value)}
              placeholder="(713) 555-0182"
            />
          </label>
          <label className="field">
            <span>Prepared By — Email</span>
            <input
              value={report.preparedBy.email}
              onChange={(e) => setField("preparedBy.email", e.target.value)}
              placeholder="reports@crownedroofing.com"
            />
          </label>
        </div>
      </section>

      {/* ROOF OVERVIEW */}
      <section className="card">
        <div className="cardHeader">
          <h2>Roof Overview</h2>
          <p>Use comma-separated roof systems. Keep it factual.</p>
        </div>

        <div className="grid3">
          <label className="field">
            <span>Roof Systems (comma separated)</span>
            <input
              value={report.roofSystemsText}
              onChange={(e) => setField("roofSystemsText", e.target.value)}
              placeholder="Standing Seam Metal, TPO"
            />
          </label>
          <label className="field">
            <span>Approx. Area</span>
            <input
              value={report.approxArea}
              onChange={(e) => setField("approxArea", e.target.value)}
              placeholder="98,000 sqft"
            />
          </label>
          <label className="field">
            <span>Access</span>
            <input
              value={report.access}
              onChange={(e) => setField("access", e.target.value)}
              placeholder="Ladder / Hatch"
            />
          </label>

          <label className="field">
            <span>Weather</span>
            <input
              value={report.weather}
              onChange={(e) => setField("weather", e.target.value)}
              placeholder="Clear, light wind"
            />
          </label>
          <label className="field">
            <span>Occupancy Sensitivity</span>
            <input
              value={report.occupancySensitivity}
              onChange={(e) => setField("occupancySensitivity", e.target.value)}
              placeholder="Warehouse / Light industrial"
            />
          </label>

          <div className="countsBox">
            <div className="countRow"><span>Immediate</span><strong>{counts.immediate}</strong></div>
            <div className="countRow"><span>Near-term</span><strong>{counts.nearTerm}</strong></div>
            <div className="countRow"><span>Monitor</span><strong>{counts.monitor}</strong></div>
          </div>
        </div>
      </section>

      {/* EXEC SUMMARY */}
      <section className="card">
        <div className="cardHeader">
          <h2>Executive Summary</h2>
          <p>3–5 bullets. These show on page 1 and print cleanly.</p>
        </div>

        <div className="bullets">
          {(report.summaryBullets || []).map((b, i) => (
            <label className="field" key={i}>
              <span>Bullet {i + 1}</span>
              <input
                value={b}
                onChange={(e) => {
                  const val = e.target.value;
                  setReport((prev) => {
                    const next = structuredClone(prev);
                    next.summaryBullets[i] = val;
                    return next;
                  });
                }}
                placeholder="Add a summary point…"
              />
            </label>
          ))}
          <button
            className="btn btnGhost"
            type="button"
            onClick={() =>
              setReport((prev) => ({ ...prev, summaryBullets: [...prev.summaryBullets, ""] }))
            }
          >
            + Add bullet
          </button>
        </div>
      </section>

      {/* FINDINGS */}
      <section className="card">
        <div className="cardHeader">
          <h2>Findings</h2>
          <p>Add findings, upload photos, write scoped recommendations.</p>
        </div>

        <div className="findings">
          {report.findings.map((f, idx) => (
            <div className="findingCard" key={f.id}>
              <div className="findingTop">
                <div className="findingLeft">
                  <div className="findingId">{f.id}</div>
                  <select
                    className="select"
                    value={f.priority}
                    onChange={(e) => setFinding(idx, "priority", e.target.value)}
                  >
                    <option>Immediate</option>
                    <option>Near-term</option>
                    <option>Monitor</option>
                  </select>
                </div>

                <div className="findingRight">
                  <button className="btn btnGhost" type="button" onClick={() => removeFinding(idx)}>
                    Remove
                  </button>
                </div>
              </div>

              <div className="grid2">
                <label className="field">
                  <span>Title</span>
                  <input
                    value={f.title}
                    onChange={(e) => setFinding(idx, "title", e.target.value)}
                    placeholder="Open seam condition at panel transition"
                  />
                </label>

                <label className="field">
                  <span>Location</span>
                  <input
                    value={f.location}
                    onChange={(e) => setFinding(idx, "location", e.target.value)}
                    placeholder="Roof Area A — West elevation, near ridge line"
                  />
                </label>

                <label className="field">
                  <span>Condition</span>
                  <textarea
                    value={f.condition}
                    onChange={(e) => setFinding(idx, "condition", e.target.value)}
                    placeholder="What you observed…"
                    rows={3}
                  />
                </label>

                <label className="field">
                  <span>Impact</span>
                  <textarea
                    value={f.impact}
                    onChange={(e) => setFinding(idx, "impact", e.target.value)}
                    placeholder="Why it matters…"
                    rows={3}
                  />
                </label>

                <label className="field span2">
                  <span>Recommendation</span>
                  <textarea
                    value={f.recommendation}
                    onChange={(e) => setFinding(idx, "recommendation", e.target.value)}
                    placeholder="Action + scope boundary…"
                    rows={3}
                  />
                </label>
              </div>

              <div className="photoBlock">
                <div className="photoTop">
                  <strong>Photos</strong>
                  <label className="btn btnGhost">
                    Upload
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      style={{ display: "none" }}
                      onChange={(e) => uploadPhotosForFinding(idx, Array.from(e.target.files || []))}
                    />
                  </label>
                </div>

                {(f.photos || []).length === 0 ? (
                  <div className="muted">No photos yet.</div>
                ) : (
                  <div className="photoGrid">
                    {f.photos.map((p, pi) => (
                      <div className="photoItem" key={p.path}>
                        {p.url ? (
                          <img src={p.url} alt="Upload preview" />
                        ) : (
                          <div className="photoMissing">Preview unavailable</div>
                        )}

                        <input
                          className="photoCaption"
                          value={p.caption || ""}
                          placeholder="Caption…"
                          onChange={(e) => updatePhotoCaption(idx, pi, e.target.value)}
                        />

                        <div className="photoPath">
                          <span>Path:</span> {p.path}
                        </div>

                        <button
                          type="button"
                          className="btn btnTiny"
                          onClick={() => removePhoto(idx, pi)}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}

          <button className="btn btnGhost" type="button" onClick={addFinding}>
            + Add finding
          </button>
        </div>
      </section>

      {/* LIMITATIONS */}
      <section className="card">
        <div className="cardHeader">
          <h2>Limitations</h2>
          <p>Keep it short. This protects you.</p>
        </div>

        <div className="bullets">
          {(report.limitations || []).map((t, i) => (
            <label className="field" key={i}>
              <span>Line {i + 1}</span>
              <input
                value={t}
                onChange={(e) => {
                  const val = e.target.value;
                  setReport((prev) => {
                    const next = structuredClone(prev);
                    next.limitations[i] = val;
                    return next;
                  });
                }}
                placeholder="Limitation line…"
              />
            </label>
          ))}

          <button
            className="btn btnGhost"
            type="button"
            onClick={() =>
              setReport((prev) => ({ ...prev, limitations: [...prev.limitations, ""] }))
            }
          >
            + Add line
          </button>
        </div>
      </section>

      {/* FOOTER ACTIONS */}
      <div className="arcBottomActions">
        <button className="btn" disabled={saving} onClick={() => save("draft")}>
          Save Draft
        </button>
        <button className="btn btnPrimary" disabled={saving} onClick={() => save("published")}>
          Publish
        </button>
        <button className="btn btnGhost" type="button" onClick={testWrite}>
        Test Write
        </button>
        <button className="btn btnGhost" type="button" disabled={saving} onClick={previewReport}>
          Preview Report
        </button>
      </div>

      <div className="arcHint">
        Storage path format used: <code>publicReports/{blobId}/photos/F-XX/...</code>
      </div>

      <div className="arcNav">
        <button className="btn btnGhost" type="button" onClick={() => nav(-1)}>
          ← Back
        </button>
      </div>
    </div>
  );
}
